import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import {
  ObjectTypeInfoColumnType,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoReferenceViewModel,
  ObjectTypeInfoViewModel,
  QueryColumnSortOrder,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ColumnTypeIcon, CoreService, FilterOption } from '@barthauer/abc/queries/opath/core';
import { _AbcScrollViewComponent, _AbcTreeComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import DevExpress from 'devextreme';
import { ItemExpandedEvent, ItemSelectionChangedEvent } from 'devextreme/ui/tree_view';
import { HeaderButton } from 'libs/abc/queries/opath/core/src/lib/viewModels/layout.viewModel';
import { NodeTypeFilter } from 'libs/abc/queries/opath/expressions-editor/src/lib/models/view.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QueriesTreeService } from '../../../services/queries-tree.service';
import { SelectColumnsService } from '../../../services/select-columns.service';
import {
  ColumnProperties,
  ColumnSpacing,
  Property,
  SelectColumsTranslation,
} from '../../../viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-select-columns',
  templateUrl: './select-columns.component.html',
  styleUrls: ['./select-columns.component.scss'],
})
export class SelectColumnsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tree') tree: _AbcTreeComponent;
  @ViewChild('scroller') scroller: _AbcScrollViewComponent;
  @Input() public set selectedNode(node: _AbcTreeNode<Property>) {
    this.currentQuery = node;
    this.refreshSelectedNode(node);
  }
  @Input() insertColumns: (columns: _AbcTreeNode<Property>[]) => void;
  @Input() closeSelectionView: () => void;
  public currentQuery: _AbcTreeNode<Property>;
  public treeData: _AbcTreeNode[] = [];
  public selectedNodes: _AbcTreeNode[] = [];
  public currentFilterOption: FilterOption;
  public showKeyColumns: boolean = true;
  public showSystemColumns: boolean = true;

  protected loading: boolean = true;
  protected neutralDesignationActive: boolean;
  protected searchButton: DevExpress.common.TextEditorButton = {
    location: 'before',
    name: 'search',
    options: { icon: 'search' },
  };
  public resAsync: Promise<ResourceSet>;
  public searchTerm: string = '';
  public searchPlaceHolder: string = '';
  public headerButtons: HeaderButton[] = [];
  public translation: SelectColumsTranslation;

  private objectTypeInfoViewModel: ObjectTypeInfoViewModel;
  private EMPTYSTRING: string = '';
  private $unsubscribe: Subject<void> = new Subject();

  constructor(
    private abcCore: AbcCore,
    private coreService: CoreService,
    private queriesTreeService: QueriesTreeService,
    private resourcesService: ResourceManagerService,
    protected selectColumnsService: SelectColumnsService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    const res = await this.resAsync;

    this.translation = {
      title: res.get('selectColumns-title'),
      keyColumnsDescription: res.get('selectColumns-keyColumnsDescription'),
      systemColumnsDescription: res.get('selectColumns-systemColumnsDescription'),
      neutralDesignation: res.get('selectColumns-neutralDesignation'),
      ok: res.get('selectColumns-ok'),
      cancel: res.get('selectColumns-cancel'),
    };

    this.selectColumnsService.searchFilter.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
      this.treeData.length && this.filterTreeByActiveKeys(this.treeData[0].children);
      this.tree.instance.repaint();
    });

    this.neutralDesignationActive = this.selectColumnsService.neutralDesignationActive;
    this.refreshButtonsState();
  }

  ngAfterViewInit(): void {
    this.tree.searchValueChange.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
      setTimeout(() => {
        this.collapseEmptyNodes(this.treeData[0]);
      }, 0);
    });
  }

  public refreshColumns(): void {}

  /**
   * Collapses a given node if it is empty and all its children if they are too.
   * @param node
   * @returns void
   */
  private collapseEmptyNodes(node: _AbcTreeNode): void {
    for (let leaf of node.children) {
      if (leaf.children.length && !leaf.children[0].visible) {
        this.tree.instance.collapseItem(leaf);
      } else {
        this.collapseEmptyNodes(leaf);
      }
    }
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  public async onItemExpanded(event: ItemExpandedEvent): Promise<void> {
    let scrollToNode: boolean = false;
    const treeNode: _AbcTreeNode = event.itemData as _AbcTreeNode;
    scrollToNode = treeNode.children.filter((child) => child.visible === false).length === 1;

    await this.expandTreeItem(treeNode);
    this.filterTreeByActiveKeys(treeNode.children);
    this.recoverScrollPosition(scrollToNode);
  }

  public toggleNeutralDesignation(): void {
    this.neutralDesignationActive = !this.neutralDesignationActive;
    this.selectColumnsService.neutralDesignationActive = this.neutralDesignationActive;

    this.translateNode(this.treeData[0]);
  }

  public searchTermChanged(event: string): void {
    this.searchTerm = event;
  }

  public filterOptionChanged(filterOption: FilterOption): void {
    this.currentFilterOption = filterOption;
  }

  public async toggleColumn(filterExpr: string): Promise<void> {
    if (filterExpr === NodeTypeFilter.Key) {
      this.showKeyColumns = !this.showKeyColumns;
    } else {
      this.showSystemColumns = !this.showSystemColumns;
    }
    await this.updateSearchFilter(filterExpr);
  }

  public insertColumnsToQuery = () => {
    this.insertColumns(
      this.selectedNodes.map((node: _AbcTreeNode) => {
        return this.createQueryColumn(node);
      })
    );
    this.emptySelection();
    this.closeSelectionView();
  };

  public emptySelection = (): void => {
    this.closeSelectionView();
    this.tree.instance.unselectAll();
    this.selectedNodes = [];
  };

  public onItemSelectionChanged = (event: ItemSelectionChangedEvent): void => {
    const updateSelection = (node: _AbcTreeNode) => {
      if (this.isParent(node)) {
        node.children.map((node: _AbcTreeNode) => {
          if (this.isParent(node)) {
            updateSelection(node);
          } else {
            unselectAll ? this.unselectNode(node) : this.selectNode(node);
          }
        });
      } else {
        toggleSelection(node);
      }
    };

    const toggleSelection = (node: _AbcTreeNode) => {
      if (this.selectedNodes.some((entry: _AbcTreeNode) => node.id && entry.id === node.id)) {
        this.unselectNode(node);
      } else {
        this.selectNode(node);
      }
    };

    const selectedNode = event.itemData as _AbcTreeNode;
    const unselectAll = this.allIncluded(selectedNode, this.selectedNodes);
    event['event'] && updateSelection(selectedNode);
  };

  private isParent = (node: _AbcTreeNode): boolean => !!node.children.length;

  /**
   * Checks if all children of a given node are included in a provided list of nodes.
   * @param node The parent node whose children are to be checked.
   * @param nodesList The list of nodes to check against.
   * @returns Returns true if all children of the node in all tree levels (recursive) are included in the nodesList, otherwise returns false.
   */
  private allIncluded = (node: _AbcTreeNode, nodesList: _AbcTreeNode[]): boolean => {
    for (let child of node.children) {
      if (this.isParent(child)) {
        return this.allIncluded(child, nodesList);
      } else {
        if (!nodesList.includes(child) && child.visible) {
          return false;
        }
      }
    }
    return true;
  };

  /**
   * Checks if a tree node element is present in the DOM.
   * @param id The node id to be checked.
   * @returns Returns true if the node element is rendered, otherwise returns false.
   */
  private nodeIsRendered = (id: string): boolean => {
    return document.querySelector(`[data-item-id="${id}"]`) ? true : false;
  };

  private selectNode = (node: _AbcTreeNode) => {
    if (!this.selectedNodes.some((entry: _AbcTreeNode) => node.id && entry.id === node.id)) {
      node.id && node['selected'] && this.selectedNodes.push(node);
    }
  };

  private unselectNode = (node: _AbcTreeNode) => {
    if (this.selectedNodes.some((entry: _AbcTreeNode) => node.id && entry.id === node.id)) {
      if (this.nodeIsRendered(node.id)) {
        this.selectedNodes.splice(
          this.selectedNodes.findIndex((entry: _AbcTreeNode) => entry.id === node.id),
          1
        );
        this.tree.instance.unselectItem(node.id);
      }
    }
  };

  public refreshSelectedNode(node: _AbcTreeNode<Property>): void {
    this.abcCore.objectSpace
      .getObjectType(node.data.name)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((objectTypeInfoViewModel) => {
        if (objectTypeInfoViewModel) {
          this.objectTypeInfoViewModel = objectTypeInfoViewModel;
          this.treeData = this.createTreeData(this.objectTypeInfoViewModel);
          this.selectColumnsService.searchFilter.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
            this.filterTreeByActiveKeys(this.treeData[0].children);
          });
        } else {
          this.treeData = [];
        }
      });
  }

  private recoverScrollPosition(scrollToNode: boolean): void {
    let scrollPosition: number = 0;
    scrollPosition = this.scroller.instance.scrollOffset();

    setTimeout(() => {
      scrollToNode ? this.scroller.instance.scrollBy(scrollPosition) : null;
    }, 1);
  }

  private async expandTreeItem(treeNode: _AbcTreeNode): Promise<void> {
    if (treeNode.data instanceof ObjectTypeInfoReferenceViewModel && treeNode.children.length === 1) {
      const objectTypeInfo: ObjectTypeInfoViewModel = treeNode.data.objectType;

      treeNode.children = [];
      this.createTreeColumns(objectTypeInfo.columnsByName, treeNode);

      this.createTreeExpandableColumns(objectTypeInfo.referencesByName, treeNode);

      for (const child of treeNode.children) {
        this.tree.addNode(child);
      }
    }
  }

  private async updateSearchFilter(filterExpr: string): Promise<void> {
    const searchFilterValue = this.selectColumnsService.searchFilter.value;

    if (searchFilterValue.includes(filterExpr)) {
      const index = searchFilterValue.indexOf(filterExpr);
      const updatedFilter = [...searchFilterValue.slice(0, index), ...searchFilterValue.slice(index + 1)];
      this.selectColumnsService.searchFilter.next(updatedFilter);
      this.selectedNodes
        .filter((node: _AbcTreeNode) => node.icon === filterExpr)
        .map((node: _AbcTreeNode) => {
          this.unselectNode(node);
        });
    } else {
      this.selectColumnsService.searchFilter.next([...searchFilterValue, filterExpr]);
    }
  }

  private filterTreeByActiveKeys(treeNodes: _AbcTreeNode[]): void {
    for (let node of treeNodes) {
      if (!this.selectColumnsService.searchFilter.value.includes(node.icon) && !node.children.length) {
        node.visible = false;
      } else {
        node.visible = true;
        this.filterTreeByActiveKeys(node.children);
      }
    }
  }

  private createTreeData(objectTypeInfo: ObjectTypeInfoViewModel): _AbcTreeNode[] {
    this.neutralDesignationActive = this.selectColumnsService.neutralDesignationActive;
    const rootNode = new _AbcTreeNode();
    rootNode.caption = this.neutralDesignationActive ? objectTypeInfo.name : objectTypeInfo.displayText;
    rootNode.id = objectTypeInfo.name;
    rootNode.icon = this.EMPTYSTRING;
    rootNode.expanded = true;
    rootNode.data = objectTypeInfo;
    this.createTreeColumns(objectTypeInfo.columnsByName, rootNode);
    this.createTreeExpandableColumns(objectTypeInfo.referencesByName, rootNode);
    return [rootNode];
  }

  private async createTreeColumns(
    columns: Map<string, ObjectTypeInfoColumnViewModel>,
    parentNode: _AbcTreeNode
  ): Promise<void> {
    columns.forEach(async (columnInfo: ObjectTypeInfoColumnViewModel) => {
      await this.createColumn(columnInfo, parentNode);
    });
  }

  private async createColumn(columnInfo: ObjectTypeInfoColumnViewModel, parentNode: _AbcTreeNode): Promise<void> {
    let treeNode = new _AbcTreeNode();
    treeNode.caption = this.neutralDesignationActive ? columnInfo.name : columnInfo.displayText;
    treeNode.id = parentNode.id + '_' + columnInfo.name;
    treeNode.parentId = parentNode.id;
    treeNode.children = [];
    treeNode.data = columnInfo;

    if (
      columnInfo.columnType === ObjectTypeInfoColumnType.IdRef ||
      columnInfo.columnType === ObjectTypeInfoColumnType.PrimaryKey ||
      columnInfo.columnType === ObjectTypeInfoColumnType.ObjectType
    ) {
      treeNode.icon = ColumnTypeIcon.key;
    } else if (columnInfo.columnType === ObjectTypeInfoColumnType.Sys) {
      treeNode.icon = ColumnTypeIcon.preferences;
    } else {
      treeNode.icon = ColumnTypeIcon.box;
    }

    treeNode.parentNode = parentNode;
    parentNode.children.push(treeNode);
  }

  private createTreeExpandableColumns(
    data: Map<string, ObjectTypeInfoReferenceViewModel>,
    parentNode: _AbcTreeNode
  ): void {
    data.forEach((element: ObjectTypeInfoReferenceViewModel) => {
      const treeNode = new _AbcTreeNode();
      treeNode.caption = this.neutralDesignationActive ? element.name : element.displayText;
      treeNode.id = parentNode.id + '_' + element.name;
      treeNode.parentId = parentNode.id;
      treeNode.children = [];
      treeNode.data = element;
      treeNode.icon = element instanceof ObjectTypeInfoReferenceViewModel ? ColumnTypeIcon.reference : null;

      const subChildren = new _AbcTreeNode();
      subChildren.caption = this.EMPTYSTRING;
      subChildren.visible = false;

      subChildren.caption = treeNode.caption;

      treeNode.children.push(subChildren);
      treeNode.parentNode = parentNode;
      parentNode.children.push(treeNode);
    });
  }

  private createQueryColumn = (node: _AbcTreeNode): _AbcTreeNode<Property> => {
    const oPathBlocks = [];
    const nameBlocks = [];

    const deduceInfos = (node: _AbcTreeNode): { alias: string; expression: string } => {
      oPathBlocks.unshift(node.id.split('_').pop());
      nameBlocks.unshift(node.caption);

      if (node.parentNode && node.parentNode !== this.treeData[0]) {
        deduceInfos(node.parentNode);
      }

      return { alias: nameBlocks.join(''), expression: oPathBlocks.join('.') };
    };

    const infos = deduceInfos(node);

    oPathBlocks.splice(0, oPathBlocks.length);
    nameBlocks.splice(0, nameBlocks.length);

    const queryColumn = new _AbcTreeNode<Property>();
    queryColumn.data = new Property();
    queryColumn.data.expression = infos?.expression;
    queryColumn.data.alias = infos?.alias;
    queryColumn.data.name = node.caption;
    queryColumn.data.columnProperties = new ColumnProperties({
      visible: true,
      sorting: QueryColumnSortOrder.None,
      spacing: ColumnSpacing.IGNOR_BLANK_PROPERTIES,
      culture: false,
      filter: false,
      formatExpression: null,
    });

    queryColumn.id = this.queriesTreeService.generatePropertyId(
      this.currentQuery.id + '_' + infos.expression,
      this.currentQuery
    );
    queryColumn.parentId = this.currentQuery.id;
    queryColumn.parentNode = this.currentQuery;
    queryColumn.icon = node.icon;
    queryColumn.caption = infos?.alias;

    return queryColumn;
  };

  private translateNode(node: _AbcTreeNode): void {
    if (node.data) {
      node.caption = this.neutralDesignationActive
        ? (node.data as ObjectTypeInfoReferenceViewModel).name
        : (node.data as ObjectTypeInfoReferenceViewModel).displayText;
    }

    if (node.children.length) {
      for (let child of node.children) {
        this.translateNode(child);
      }
    }
  }

  public refreshButtonsState() {
    this.headerButtons = [
      {
        icon: 'key',
        toolTipContent: this.translation?.keyColumnsDescription,

        statfullButton: true,
        active: this.showKeyColumns,
        action: async () => this.toggleColumn(NodeTypeFilter.Key),
      },
      {
        icon: 'preferences',
        toolTipContent: this.translation?.systemColumnsDescription,
        statfullButton: true,
        active: this.showSystemColumns,
        action: async () => this.toggleColumn(NodeTypeFilter.system),
      },
      {
        icon: 'fontsize',
        toolTipContent: this.translation?.neutralDesignation,
        statfullButton: true,
        active: this.neutralDesignationActive,
        action: async () => this.toggleNeutralDesignation(),
      },
    ];
  }
}
